import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import type { EventGuest } from '../../types/eventGuest';
import { EVENT_GUEST_RELATIONSHIP_LABELS } from '../../types/eventGuest';

interface DuplicateGuestDialogProps {
  open: boolean;
  newGuest: EventGuest;
  existingGuestId: string;
  existingGuests: EventGuest[];
  onAddAnyway: () => void;
  onUseExisting: () => void;
  onCancel: () => void;
}

/**
 * Plan 4.5 — Soft-warn dialog shown after the API returns
 * `{warning, existingGuestId}`. The create has already succeeded;
 * the user picks whether to keep both, replace with the existing,
 * or back out entirely (which triggers a delete of the just-created
 * guest in the caller).
 */
const DuplicateGuestDialog: React.FC<DuplicateGuestDialogProps> = ({
  open,
  newGuest,
  existingGuestId,
  existingGuests,
  onAddAnyway,
  onUseExisting,
  onCancel,
}) => {
  const existing = existingGuests.find((g) => g.id === existingGuestId);

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600 }}>Possible duplicate</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          You already have a guest with this name. Would you like to use the
          existing entry or keep both?
        </DialogContentText>
        <Stack direction="row" spacing={3} sx={{ mb: 1 }}>
          <Stack sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">
              New
            </Typography>
            <Typography fontWeight={600}>
              {newGuest.firstName} {newGuest.lastName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {EVENT_GUEST_RELATIONSHIP_LABELS[newGuest.relationship]}
            </Typography>
          </Stack>
          {existing && (
            <Stack sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Existing
              </Typography>
              <Typography fontWeight={600}>
                {existing.firstName} {existing.lastName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {EVENT_GUEST_RELATIONSHIP_LABELS[existing.relationship]}
              </Typography>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onCancel} color="inherit">
          Cancel
        </Button>
        <Button onClick={onAddAnyway} color="primary">
          Add anyway
        </Button>
        <Button onClick={onUseExisting} variant="contained" autoFocus>
          Use existing
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateGuestDialog;
